import { RwGlyph, RwRing, RwCurrency } from '@components/atoms';
import { RwLoading } from '@components/RwLoading';
import { t } from '@services/i18n';
import { useApiQuery } from '@hooks/useApiQuery';
import { RwEmpty } from '@components/RwEmpty';
import type { Campaign } from '@shared/types';
import type { MemberScreenProps } from './types';

interface CampaignsResponse {
  campaigns: Campaign[];
}

/**
 * Campaigns - active multi-step journeys with a progress ring, a step
 * checklist, and the reward granted on completion. Campaigns scoped to a later
 * release (those that would use Event Credits) are hidden, matching the design.
 * Renders only real data from the `campaigns` endpoint.
 */
export function UserCampaigns(_props: MemberScreenProps) {
  const { data, isLoading } = useApiQuery<CampaignsResponse>(['rw-campaigns'], 'campaigns', {
    fallback: { campaigns: [] },
  });
  if (isLoading) return <RwLoading variant="cards" />;
  const all = data?.campaigns ?? [];
  const active = all.filter((q) => q.release !== 'Later');

  return (
    <div className="rw-page rw-user">
      <header className="rw-page-h">
        <div>
          <div className="rw-eyebrow">{t('COM_REWARDIFY_UCAMP_EYEBROW', 'Campaigns')}</div>
          <h1 className="rw-h1">{t('COM_REWARDIFY_UCAMP_TITLE', 'Active campaigns')}</h1>
          <p className="rw-lede">{t('COM_REWARDIFY_UCAMP_LEDE', 'Multi-step journeys with bonus rewards. Optional - join what interests you.')}</p>
        </div>
      </header>

      <div className="rw-quest-grid">
        {active.length === 0 ? (
          <RwEmpty
            title={t('COM_REWARDIFY_UCAMP_EMPTY', 'No active campaigns')}
            hint={t('COM_REWARDIFY_UCAMP_EMPTY_HINT', 'New campaigns will appear here when they launch.')}
          />
        ) : (
          active.map((q) => (
          <div key={q.id} className={`rw-quest-card rw-quest-${q.tone}`}>
            <div className="rw-quest-h">
              <div className="rw-quest-icon">
                <RwGlyph kind={q.icon} size={18} />
              </div>
              <div style={{ flex: 1 }}>
                <div className="rw-quest-tone">
                  {q.tone}
                  {q.ends && ` · ${t('COM_REWARDIFY_UCAMP_ENDS', 'ends %s', q.ends)}`}
                </div>
                <h3 className="rw-quest-name">{q.name}</h3>
              </div>
              <RwRing value={q.progress} size={40} thickness={4} label={`${Math.round(q.progress * 100)}%`} />
            </div>
            <p className="rw-quest-desc">{q.desc}</p>
            <div className="rw-quest-steps">
              {q.steps.map((s, i) => (
                <div key={`${q.id}-${i}`} className={`rw-quest-step ${s.done ? 'done' : ''}`}>
                  <span className="rw-quest-check">{s.done ? '✓' : i + 1}</span>
                  <span style={{ flex: 1 }}>{s.label}</span>
                  {s.count && (
                    <span className="rw-mono rw-fg2" style={{ fontSize: 11 }}>
                      {s.count}
                    </span>
                  )}
                </div>
              ))}
            </div>
            <div className="rw-quest-reward">
              <div className="rw-stat-label" style={{ marginBottom: 6 }}>
                {t('COM_REWARDIFY_UCAMP_REWARD_ON_COMPLETION', 'Reward on completion')}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                <RwCurrency id="points" amount={q.reward.points} />
                <RwCurrency id="reputation" amount={q.reward.rep} />
                {q.reward.badge && <span className="rw-pill rw-pill-badge">{t('COM_REWARDIFY_UCAMP_BADGE', 'badge')}</span>}
              </div>
            </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
